import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Button, Group } from "@mantine/core";
import { useDocumentTitle } from "@mantine/hooks";
import {
  Root as DialogRoot,
  Trigger as DialogTrigger,
} from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import { IconFilter } from "@tabler/icons-react";
import { useShallow } from "zustand/react/shallow";

import { WithPatientNavbar } from "@layouts/WithPatientNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { TabsContent, TabsList, TabsRoot } from "@components/Tabs";

import {
  FilterableData,
  filterByDay,
  // filterByFileType,
  filterByMonth,
  filterByPhysicians,
  filterByYear,
} from "@utils/filterDialogFilters";
import { truncateWithEllipsis } from "@utils/truncateText";

import { MAX_PAGE_SIZE, PAGE_TITLE } from "@constants/index";

import { useGetConsultationsForPatient } from "@hooks/queries/consultation/useGetConsultationsForPatient";
import { useGetImmunizationsForPatient } from "@hooks/queries/immunization/useGetImmunizationsForPatient";
import { useGetLabsForPatient } from "@hooks/queries/lab/useGetLabsForPatient";
import { useGetPrescriptionsForPatient } from "@hooks/queries/prescription/useGetPrescriptionsForPatient";
import { useGetRadiologyForPatient } from "@hooks/queries/radiology/useGetRadiologyForPatient";
import { useAppStore } from "@hooks/store/useAppStore";

// import { useGetReferralsForPatient } from "@hooks/queries/referral/useGetReferralsForPatient";
import { Consultation } from "@customTypes/consultation";
import { Immunization } from "@customTypes/immunization";
import { Laboratory } from "@customTypes/laboratory";
import { Prescription } from "@customTypes/prescription";
import { Radiology } from "@customTypes/radiology";
import { BasicUser } from "@customTypes/user";

// import { Referral } from "@customTypes/referral";
import { ConsultationTab } from "./components/ConsultationTab";
import { DocumentationFilterDialog } from "./components/DocumentationFilterDialog";
import { ImmunizationTab } from "./components/ImmunizationTab";
import { LabTab } from "./components/LabTab";
import { PrescriptionTab } from "./components/PrescriptionTab";
import { RadiologyTab } from "./components/RadiologyTab";
// import { ReferralsTab } from "./components/ReferralsTab";
import { StyledDocumentation } from "./Documentation.styled";

// type DocumentationPage =
//   | "consultation"
//   | "laboratory"
//   | "radiology"
//   | "prescription"
//   | "immunization"
//   | "referrals";

export const Documentation = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("records")} • ${PAGE_TITLE}`);
  const [storeTabs, setStoreTabs] = useAppStore(
    useShallow(state => [state.tabs, state.setTabs])
  );

  const [page, setPage] = useState<
    | "consultation"
    | "laboratory"
    | "radiology"
    | "prescription"
    | "immunization"
    | undefined
    | string
  >(storeTabs?.recordsTab);
  const [filters, setFilters] = useState({
    year: String(new Date().getFullYear()),
    month: "",
    day: "",
    physician: "",
    // fileType: "",
  });
  const [isFiltered, setIsFiltered] = useState(false);
  const [filteredData, setFilteredData] = useState<FilterableData[] | []>([]);
  const [physicians, setPhysicians] = useState<Set<string>>(new Set());

  const { patientId } = useParams();
  const {
    isLoading: isConsultationLoading,
    data: consultationData,
    isError: isConsultationError,
    isSuccess: isConsultationSuccess,
  } = useGetConsultationsForPatient({
    patientId: patientId as string,
    withFilesOnly: true,
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE, // todo: Change this for this page later
    },
  });
  const {
    isLoading: isLabsLoading,
    data: labsData,
    isError: isLabsError,
    isSuccess: isLabsSuccess,
  } = useGetLabsForPatient({
    patientId: patientId as string,
    withFilesOnly: true,
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE, // todo: Change this for this page later
    },
  });
  const {
    isLoading: isRadiologyLoading,
    data: radiologyData,
    isError: isRadiologyError,
    isSuccess: isRadiologySuccess,
  } = useGetRadiologyForPatient({
    patientId: patientId as string,
    withFilesOnly: true,
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE, // todo: Change this for this page later
    },
  });
  const {
    isLoading: isPrescriptionsLoading,
    data: prescriptionsData,
    isError: isPrescriptionsError,
    isSuccess: isPrescriptionsSuccess,
  } = useGetPrescriptionsForPatient({
    patientId: patientId as string,
    withFilesOnly: true,
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE, // todo: Change this for this page later
    },
  });
  const {
    isLoading: isImmunizationsLoading,
    data: immunizationsData,
    isError: isImmunizationsError,
    isSuccess: isImmunizationSuccess,
  } = useGetImmunizationsForPatient({
    patientId: patientId as string,
    withFilesOnly: true,
    pagination: {
      page: 1,
      pageSize: MAX_PAGE_SIZE, // todo: Change this for this page later
    },
  });
  // const {
  //   isLoading: isReferralsLoading,
  //   data: referralsData,
  //   isError: isReferralsError,
  //   isSuccess: isReferralsSuccess,
  // } = useGetReferralsForPatient({
  //   id: patientId as string,
  //   withFilesOnly: true,
  // });

  // console.log(labsData);
  useEffect(() => {
    let localFilteredData: FilterableData[] = [];

    // Page filters ------------------------------------------------
    if (page === "consultation" && consultationData) {
      setFilteredData(consultationData.newData);
      localFilteredData = consultationData.newData;
    } else if (page === "laboratory" && labsData) {
      setFilteredData(labsData.newData);
      localFilteredData = labsData.newData;
    } else if (page === "radiology" && radiologyData) {
      setFilteredData(radiologyData.newData);
      localFilteredData = radiologyData.newData;
    } else if (page === "prescription" && prescriptionsData) {
      setFilteredData(prescriptionsData.newData);
      localFilteredData = prescriptionsData.newData;
    } else if (page === "immunization" && immunizationsData) {
      setFilteredData(immunizationsData.newData);
      localFilteredData = immunizationsData.newData;
    }
    // else if (page === "Referrals" && referralsData) {
    //   setFilteredData(referralsData);
    //   localFilteredData = referralsData;
    // }

    // Actual filters start here -------------------------------------
    if (filters.year) {
      localFilteredData = filterByYear(filters.year, localFilteredData);
      // console.log(`Filtering ${page} by ${filters.year}`, localFilteredData);
    }

    if (filters.month) {
      localFilteredData = filterByMonth(filters.month, localFilteredData);
      // console.log(`Filtering ${page} by ${filters.month}`, localFilteredData);
    }

    if (filters.day) {
      localFilteredData = filterByDay(filters.day, localFilteredData);
      // console.log(`Filtering ${page} by ${filters.day}`, localFilteredData);
    }

    if (filters.physician) {
      localFilteredData = filterByPhysicians(
        filters.physician,
        localFilteredData
      );
    }

    // if (filters.fileType) {
    //   switch (filters.fileType) {
    //     case "Image":
    //       localFilteredData = filterByFileType("Image", localFilteredData);
    //       break;
    //     case "Report":
    //       localFilteredData = filterByFileType("Report", localFilteredData);
    //       break;
    //     default:
    //       break;
    //   }
    //   // console.log(
    //   //   `Filtering ${page} by ${filters.fileType}`,
    //   //   localFilteredData
    //   // );
    // }

    setFilteredData(localFilteredData);
    setIsFiltered(true);

    if (
      !filters.year &&
      !filters.month &&
      !filters.day &&
      //. !filters.fileType &&
      !filters.physician
    ) {
      switch (page) {
        case "consultation":
          setFilteredData(consultationData?.newData || []);
          break;
        case "laboratory":
          setFilteredData(labsData?.newData || []);
          break;
        case "prescription":
          setFilteredData(prescriptionsData?.newData || []);
          break;
        case "immunization":
          setFilteredData(immunizationsData?.newData || []);
          break;

        default:
          break;
      }
      setIsFiltered(false);
      // console.log("Resetting filtered data");
    }

    // Physicians data ----------------------------------------
    if (
      consultationData ||
      labsData ||
      radiologyData ||
      prescriptionsData ||
      immunizationsData
      // referralsData
    ) {
      const allData = [
        ...(consultationData?.newData || []),
        ...(labsData?.newData || []),
        ...(radiologyData?.newData || []),
        ...(prescriptionsData?.newData || []),
        ...(immunizationsData?.newData || []),
        // ...(referralsData || []),
      ];

      const physiciansFromData = new Set<string>();

      for (const i of allData) {
        if (typeof i === "object" && "physician" in i && i.physician) {
          physiciansFromData.add(i.physician?.fullName);
        } else if (typeof i === "object" && "orderedBy" in i && i.orderedBy) {
          physiciansFromData.add((i.orderedBy as BasicUser).fullName);
        } else if (
          typeof i === "object" &&
          "prescribedBy" in i &&
          i.prescribedBy
        ) {
          physiciansFromData.add(i.prescribedBy?.fullName);
        } else if (
          typeof i === "object" &&
          "administeredBy" in i &&
          i.administeredBy
        ) {
          physiciansFromData.add(i.administeredBy?.fullName);
        }
        // else if (typeof i === "object" && "doctor" in i && i.doctor) {
        //   physiciansFromData.add(i.doctor);
        // }
      }

      setPhysicians(physiciansFromData);
      // console.log(physiciansFromData);
    }
  }, [
    filters.year,
    filters.month,
    filters.day,
    // filters.fileType,
    filters.physician,
    page,
    consultationData,
    labsData,
    radiologyData,
    prescriptionsData,
    immunizationsData,
    // referralsData,
  ]);

  return (
    <WithSidebar hasPatientNavbar>
      <WithPatientNavbar>
        <StyledDocumentation>
          <DialogRoot>
            <DocumentationFilterDialog
              filters={filters}
              setFilters={setFilters}
              physicians={physicians}
            />
            <TabsRoot
              value={page}
              onValueChange={val => {
                setPage(val as typeof page);
                setStoreTabs({ recordsTab: val });
              }}>
              <div className="header">
                <h1>{t("records")}</h1>

                <TabsList
                  comboboxItems={[
                    { label: t("consultation"), value: "consultation" },
                    { label: t("laboratory"), value: "laboratory" },
                    { label: t("radiology"), value: "radiology" },
                    { label: t("prescription"), value: "prescription" },
                    { label: t("immunization"), value: "immunization" },
                  ]}
                />
              </div>

              <div className="table-header">
                {filters.year ||
                filters.month ||
                filters.day ||
                // filters.fileType
                filters.physician ? (
                  <>
                    <p>{t("applied-filters")}</p>

                    <ul className="filter-tags">
                      {[
                        {
                          filterTitle: "year",
                          filterValue: filters.year,
                        },
                        {
                          filterTitle: "month",
                          filterValue: filters.month,
                        },
                        {
                          filterTitle: "day",
                          filterValue: filters.day,
                        },
                        {
                          filterTitle: "physician",
                          filterValue: filters.physician,
                        },
                        // {
                        //   filterTitle: "fileType",
                        //   filterValue: filters.fileType,
                        // },
                      ]
                        .filter(item => item.filterValue.length)
                        .map(({ filterTitle, filterValue }, index) => (
                          <li className="filter-tags-item" key={index}>
                            <span>
                              {filterTitle === "physician" ? (
                                truncateWithEllipsis(filterValue, 15)
                              ) : filterTitle === "year" ? (
                                <span>{filterValue}</span>
                              ) : (
                                <span>{t(`${filterValue}`)}</span>
                              )}
                            </span>
                            <button
                              onClick={() => {
                                if (filterTitle === "year")
                                  setFilters({ ...filters, year: "" });
                                else if (filterTitle === "month")
                                  setFilters({ ...filters, month: "" });
                                else if (filterTitle === "day")
                                  setFilters({ ...filters, day: "" });
                                else if (filterTitle === "physician")
                                  setFilters({ ...filters, physician: "" });
                                // else if (filterTitle === "fileType")
                                //   setFilters({ ...filters, fileType: "" });
                              }}>
                              <Cross2Icon />
                            </button>
                          </li>
                        ))}
                    </ul>
                  </>
                ) : (
                  <p>{t("no-filters")}</p>
                )}

                <Group w="100%" justify="end">
                  {/* // todo: Before replacing this, use tableData state in all tabs to prevent "Add lab" modal issue */}
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      leftSection={<IconFilter size={16} />}>
                      {t("filters")}
                    </Button>
                  </DialogTrigger>
                </Group>
              </div>

              <TabsContent value="consultation">
                <ConsultationTab
                  isLoading={isConsultationLoading}
                  isSuccess={isConsultationSuccess}
                  isError={isConsultationError}
                  data={filteredData as Consultation[]}
                  isFiltered={isFiltered}
                />
              </TabsContent>
              <TabsContent value="laboratory">
                <LabTab
                  isLoading={isLabsLoading}
                  isSuccess={isLabsSuccess}
                  isError={isLabsError}
                  data={filteredData as Laboratory[]}
                  isFiltered={isFiltered}
                />
              </TabsContent>
              <TabsContent value="radiology">
                <RadiologyTab
                  isLoading={isRadiologyLoading}
                  isSuccess={isRadiologySuccess}
                  isError={isRadiologyError}
                  data={filteredData as Radiology[]}
                  isFiltered={isFiltered}
                />
              </TabsContent>
              <TabsContent value="prescription">
                <PrescriptionTab
                  isLoading={isPrescriptionsLoading}
                  isSuccess={isPrescriptionsSuccess}
                  isError={isPrescriptionsError}
                  data={filteredData as Prescription[]}
                  isFiltered={isFiltered}
                />
              </TabsContent>
              <TabsContent value="immunization">
                <ImmunizationTab
                  isLoading={isImmunizationsLoading}
                  isSuccess={isImmunizationSuccess}
                  isError={isImmunizationsError}
                  data={filteredData as Immunization[]}
                  isFiltered={isFiltered}
                />
              </TabsContent>
              {/* <TabsContent value="referrals">
                <ReferralsTab
                  isLoading={isReferralsLoading}
                  isSuccess={isReferralsSuccess}
                  isError={isReferralsError}
                  data={filteredData as Referral[]}
                  isFiltered={isFiltered}
                />
              </TabsContent> */}
            </TabsRoot>
          </DialogRoot>
        </StyledDocumentation>
      </WithPatientNavbar>
    </WithSidebar>
  );
};
